import { Button } from "@/components/ui/button";
import { RefreshCw, Receipt, Bell } from "lucide-react";
import { TableBill, TableOrderItem } from "@/hooks/usePublicMenu";
import { CallWaiterButton } from "@/components/public/CallWaiterButton";

interface TabletBillSectionProps {
  bill: TableBill | null;
  orderItems: TableOrderItem[];
  tableNumber: string;
  restaurantId: string;
  onRefresh: () => void;
}

export function TabletBillSection({
  bill,
  orderItems,
  tableNumber,
  restaurantId,
  onRefresh,
}: TabletBillSectionProps) {
  if (!bill) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <Receipt className="w-20 h-20 text-slate-600 mb-6" />
        <h2 className="text-2xl font-bold text-white mb-2">Nenhum pedido aberto</h2>
        <p className="text-slate-400 mb-6">
          Adicione itens do cardápio para começar
        </p>
        <Button variant="outline" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white">Sua Conta</h2>
            <p className="text-slate-400">Mesa {tableNumber}</p>
          </div>
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>

        {/* Items */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
          <div className="divide-y divide-slate-700">
            {orderItems.map((item) => (
              <div key={item.id} className="p-4 flex justify-between items-center">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <span className="text-primary font-medium">
                      {item.quantity}x
                    </span>
                    <span className="text-white font-medium truncate">
                      {item.product_name}
                    </span>
                  </div>
                  {item.notes && (
                    <p className="text-sm text-slate-400 ml-8 truncate">
                      {item.notes}
                    </p>
                  )}
                </div>
                <span className="text-white font-medium shrink-0 ml-4">
                  R$ {item.total_price.toFixed(2).replace(".", ",")}
                </span>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="border-t border-slate-600 p-4 space-y-2 bg-slate-800/80">
            <div className="flex justify-between text-slate-400">
              <span>Subtotal</span>
              <span>R$ {bill.subtotal.toFixed(2).replace(".", ",")}</span>
            </div>
            {bill.discount > 0 && (
              <div className="flex justify-between text-green-400">
                <span>Desconto</span>
                <span>- R$ {bill.discount.toFixed(2).replace(".", ",")}</span>
              </div>
            )}
            {bill.additional_fees > 0 && (
              <div className="flex justify-between text-slate-400">
                <span>Taxa de serviço</span>
                <span>R$ {bill.additional_fees.toFixed(2).replace(".", ",")}</span>
              </div>
            )}
            <div className="flex justify-between text-xl font-bold text-white pt-2 border-t border-slate-600">
              <span>Total</span>
              <span className="text-primary">
                R$ {bill.total.toFixed(2).replace(".", ",")}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 space-y-3">
          <div className="w-full">
            <CallWaiterButton
              restaurantId={restaurantId}
              tableNumber={tableNumber}
            />
          </div>
          <p className="text-center text-sm text-slate-500">
            O garçom virá até sua mesa para fechar a conta
          </p>
        </div>
      </div>
    </div>
  );
}
